<script>
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({ layout: CRMDrawer });
</script>
<script setup>
import { ref, computed } from "vue";
import { router, usePage } from "@inertiajs/vue2";
import AppLayout from "@/Layouts/AppLayout.vue";
import { groupBy } from "lodash";
import TipTapReader from "../../Components/Editor/TipTapReader.vue";
import axios from "axios";

import CRMDrawer from "../../Layouts/CRMDrawer.vue";

;

const props = defineProps({
    campaign: {
        type: Object,
        default: () => ({}),
    },
    crmClient: {
        type: Object,
        default: () => ({}),
    },
    campaignSubscription: {
        type: Object,
        default: () => ({}),
    },
    messages: {
        type: Array,
        default: () => [],
    },
    day: {
        type: Number,
        default: null,
    },
});
const search = ref("");
const selectedDay = ref(props.day ?? null);
const selectedChannel = ref(null);
const loading = ref(false);
const selectionLoading = ref(false);

const activeCampaign = computed(() => {
    if (!props.campaignSubscription) return {};
    return props.campaignSubscription;
});

//const campaignSubscriptions = ref([]);

/*const fetchCampaignSubscriptions = async () => {
    loading.value = true;
    try {
        const response = await axios.get(
            `/campaign-subscriptions/active/${props.campaign.id}`
        );
        campaignSubscriptions.value = response.data;
    } catch (error) {
        console.error("Error fetching campaign subscriptions:", error);
    } finally {
        loading.value = false;
    }
};*/

const dayOptions = computed(() => {
    const uniqueDays = [
        ...new Set(
            props.campaign.messages.map((message) => message.day_offset)
        ),
    ];
    return uniqueDays.sort((a, b) => a - b); // Sort days in ascending order
});

const channelOptions = computed(() => {
    const uniqueChannels = [
        ...new Set(props.campaign.messages.map((message) => message.channel)),
    ];
    return uniqueChannels.sort(); // Sort channels alphabetically
});

const filteredMessages = computed(() => {
    return props.messages.filter((message) => {
        const matchesDay =
            selectedDay.value === null ||
            message.day_offset === selectedDay.value;
        const matchesChannel =
            selectedChannel.value === null ||
            message.channel === selectedChannel.value;
        return matchesDay && matchesChannel;
    });
});

function shouldSend() {
    if (!activeCampaign.value?.id || !selectedDay.value) {
        return;
    }
    selectionLoading.value = true;
    router.get(
        `/automations/${activeCampaign.value.id}/preview/${selectedDay.value}`,
        {},
        {
            preserveState: true,
            preserveScroll: true,
            onFinish: () => {
                selectionLoading.value = false;
            },
        }
    );
}

function clear() {
    router.get(
        `/campaigns/${props.campaign.id}`,
        {},
        {
            preserveState: true,
            preserveScroll: true,
        }
    );
}

//fetchCampaignSubscriptions();
</script>

<template>
    <AppLayout
        page="/campaigns"
        :backLink="activeCampaign?.id ? '/dashboard' : '/campaigns'"
        pageTitle="Automations"
        ><template>
            <v-card-title class="soli-grey py-0">{{
                campaign.description
            }}</v-card-title>
        </template>
        <v-container fluid>
            <v-data-iterator
                :items="filteredMessages"
                :search="search"
                group-by="day_offset"
                hide-default-footer
                disable-pagination
            >
                <template v-slot:header>
                    <div class="flex justify-start w-full mb-8 gap-4">
                        <!-- Search -->
                        <v-text-field
                            v-model="search"
                            clearable
                            outlined
                            dense
                            hide-details
                            label="Search"
                            class="grow"
                        ></v-text-field>

                        <!-- Channel Filter -->
                        <v-select
                            v-model="selectedChannel"
                            :items="[null, ...channelOptions]"
                            :item-text="
                                (item) =>
                                    item === null ? 'All Channels' : item
                            "
                            :item-value="(item) => item"
                            label="Channel"
                            clearable
                            outlined
                            dense
                            hide-details
                            class="shrink"
                        ></v-select>

                        <!-- Day Filter -->
                        <v-select
                            v-model="selectedDay"
                            :items="[null, ...dayOptions]"
                            :item-text="
                                (item) =>
                                    item === null ? 'All Days' : `Day ${item}`
                            "
                            :item-value="(item) => item"
                            label="Day"
                            clearable
                            outlined
                            dense
                            hide-details
                            class="shrink"
                            @input="shouldSend()"
                            @click:clear="clear()"
                        ></v-select>

                        <!-- Campaign Subscription -->
                        <!--<v-select
                            v-model="activeCampaign"
                            :items="[activeCampaign]"
                            item-text="studentable.name"
                            item-value="id"
                            return-object
                            label="Active Automation"
                            clearable
                            solo
                            hide-details
                            :loading="loading"
                            class="shrink w-1/4"
                            @input="shouldSend()"
                            @click:clear="clear()"
                        >
                        </v-select>-->
                    </div>
                </template>
                <template v-slot:default="props">
                    <div v-for="(group, idx) in props.groupedItems" :key="idx">
                        <v-card-title :class="{ 'mt-2': idx > 0 }"
                            >Day {{ group.name }}</v-card-title
                        >
                        <v-row>
                            <v-col
                                v-for="(message, i) in group.items"
                                :key="message.id"
                                cols="12"
                                md="4"
                                lg="4"
                                xl="3"
                            >
                                <v-card class="soli-round">
                                    <v-btn
                                        v-if="is('Admin')"
                                        icon
                                        small
                                        color="dark"
                                        class="right-3 top-3 !absolute"
                                        :href="`/admin/campaign-message/${message.id}/edit`"
                                        ><v-icon small>edit</v-icon></v-btn
                                    >
                                    <v-card-text class="flex flex-col gap-2">
                                        <strong>Message # {{ i + 1 }}</strong>
                                        <div class="flex items-center gap-2">
                                            <strong>Channel: </strong>
                                            <v-chip
                                                x-small
                                                class="font-bold px-2"
                                                :color="
                                                    message.channel === 'email'
                                                        ? 'accent'
                                                        : 'secondary'
                                                "
                                                >{{ message.channel }}</v-chip
                                            >
                                        </div>
                                        <div>
                                            <strong>Subject: </strong>
                                            {{ message.subject }}
                                        </div>
                                        <div v-if="message.description">
                                            <strong>Description: </strong>
                                            {{ message.description }}
                                        </div>
                                        <div
                                            v-if="
                                                activeCampaign?.id &&
                                                selectedDay &&
                                                !selectionLoading
                                            "
                                            class="flex items-center"
                                        >
                                            <strong>Should Send: </strong>
                                            <v-icon
                                                small
                                                class="ml-2 mr-1"
                                                :color="
                                                    message.shouldSend
                                                        ? 'success'
                                                        : 'error'
                                                "
                                                >{{
                                                    message.shouldSend
                                                        ? "check_circle"
                                                        : "block"
                                                }}</v-icon
                                            >{{ message.shouldSend }}
                                        </div>
                                    </v-card-text>
                                    <v-divider />

                                    <!-- Content and Alt Content in Expansion Panels -->
                                    <v-card-text
                                        v-if="!message.finalContent"
                                        class="px-0"
                                    >
                                        <strong class="px-4">Content</strong>

                                        <v-expansion-panels flat accordion>
                                            <v-expansion-panel>
                                                <v-expansion-panel-header
                                                    >Primary</v-expansion-panel-header
                                                >
                                                <v-expansion-panel-content
                                                    ><TipTapReader
                                                        :value="message.content"
                                                    ></TipTapReader
                                                ></v-expansion-panel-content>
                                            </v-expansion-panel>
                                            <v-expansion-panel
                                                v-if="message.alt_content"
                                            >
                                                <v-expansion-panel-header
                                                    >Alternative</v-expansion-panel-header
                                                >
                                                <v-expansion-panel-content
                                                    ><TipTapReader
                                                        :value="message.content"
                                                    ></TipTapReader
                                                ></v-expansion-panel-content>
                                            </v-expansion-panel>
                                        </v-expansion-panels>
                                    </v-card-text>
                                    <v-card-text v-else>
                                        <strong>Content</strong>
                                        <TipTapReader
                                            :value="message.finalContent"
                                        ></TipTapReader>
                                    </v-card-text>
                                    <!-- Workflow Logic Visualization -->
                                    <v-divider v-if="message.conditions" />
                                    <v-card-text
                                        v-if="message.conditions"
                                        class="px-0"
                                    >
                                        <strong class="px-4"
                                            >Workflow Logic</strong
                                        >
                                        <v-expansion-panels flat>
                                            <v-expansion-panel
                                                v-for="(
                                                    condition, idx
                                                ) in message.conditions"
                                                :key="idx"
                                            >
                                                <v-expansion-panel-header
                                                    >Condition
                                                    {{
                                                        idx + 1
                                                    }}</v-expansion-panel-header
                                                >
                                                <v-expansion-panel-content>
                                                    <p>
                                                        <strong>Field: </strong>
                                                        {{ condition.field }}
                                                    </p>
                                                    <p>
                                                        <strong
                                                            >Operator:
                                                        </strong>
                                                        {{ condition.operator }}
                                                    </p>
                                                    <p>
                                                        <strong>Value: </strong>
                                                        {{ condition.value }}
                                                    </p>
                                                    <p>
                                                        <strong
                                                            >onSuccess:
                                                        </strong>
                                                        {{
                                                            condition.onSuccess
                                                        }}
                                                    </p>
                                                    <p>
                                                        <strong
                                                            >onFailure:
                                                        </strong>
                                                        {{
                                                            condition.onFailure
                                                        }}
                                                    </p>
                                                    <p>
                                                        <strong
                                                            >callbackData:
                                                        </strong>
                                                        {{
                                                            condition.callbackData
                                                        }}
                                                    </p>
                                                </v-expansion-panel-content>
                                            </v-expansion-panel>
                                        </v-expansion-panels>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </div>
                </template>
            </v-data-iterator>
        </v-container>
    </AppLayout>
</template>
